import { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";

export const AutoTopupCard = ({ workspaceId, canManage }) => {
  const { L } = useApp();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/payments/auto-topup", { params: { workspace_id: workspaceId } }).then((r) => setS(r.data)).catch(() => {}); }, [workspaceId]);
  if (!s) return null;
  const save = async (patch) => {
    const next = { ...s, ...patch };
    try {
      const { data } = await api.put("/payments/auto-topup", { workspace_id: workspaceId, minutes: next.minutes, documents: next.documents, max_per_month: next.max_per_month });
      setS(data);
      toast.success(L("Tetapan auto tambah disimpan", "Auto top-up settings saved"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const off = !canManage || !s.eligible;
  const row = (k, ms, en) => (
    <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-sm">
      <span>{L(ms, en)}</span>
      <Switch checked={s[k]} disabled={off} onCheckedChange={(v) => save({ [k]: v })} data-testid={`auto-topup-${k}-switch`} />
    </label>
  );
  return (
    <Card className="mt-6 p-6" data-testid="auto-topup-card">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-600"><Zap size={20} /></span>
        <div>
          <h3 className="font-bold">{L("Tambah kuota automatik", "Auto top-up")}</h3>
          <p className="mt-1 text-sm text-slate-500">{L("Kad langganan anda dicaj RM9.90 secara automatik bila baki hampir habis, supaya mesyuarat seterusnya tidak tersekat.", "Your subscription card is charged RM9.90 automatically when quota runs low, so your next meeting isn't blocked.")}</p>
        </div>
      </div>
      {!s.eligible && <p className="mt-4 rounded-xl bg-slate-50 p-3 text-xs text-slate-500" data-testid="auto-topup-ineligible">{L("Tersedia untuk pelan berbayar yang dilanggan melalui kad (Stripe).", "Available for paid plans subscribed by card (Stripe).")}</p>}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {row("minutes", "+100 minit bila baki < 15 minit", "+100 minutes when < 15 min left")}
        {row("documents", "+20 dokumen bila baki < 3", "+20 documents when < 3 left")}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <span>{L("Had sekitaran", "Limit per cycle")}:</span>
        <Select value={String(s.max_per_month)} disabled={off} onValueChange={(v) => save({ max_per_month: Number(v) })}>
          <SelectTrigger className="h-9 w-48 rounded-xl" data-testid="auto-topup-max-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[1, 3, 5, 10].map((n) => <SelectItem key={n} value={String(n)} data-testid={`auto-topup-max-${n}`}>{n}× ({L("maks", "max")} RM{(n * 9.9).toFixed(2)})</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-500" data-testid="auto-topup-used">{L(`Digunakan kitaran ini: ${s.used_this_month}/${s.max_per_month}`, `Used this cycle: ${s.used_this_month}/${s.max_per_month}`)}</span>
      </div>
    </Card>
  );
};
